<template>
  <div>
    <div :class="[{'smart-input_one-row': oneRow}, sizeClass]" class="smart-input smart-input_vselect">
      <div v-if="caption" class="smart-input__name">
        {{ caption }}
      </div>
      <div :class="{'input_error':error}" class="smart-input__wrapper">
        <template v-if="isNeededRemoteSearch">
          <!--
          Срабатывает в случае использования удаленного поиска.
          @event search:focus
          -->
          <v-select
            v-model="newValue"
            :disabled="disabled"
            :filterable="false"
            :multiple="multiple"
            :options="optionsForSelect"
            :placeholder="placeholder"
            class="smart-input__select"
            @search="(search, loading) => {loading(true); debouncedRemoteSearch(search, () => {loading(false)});}"
            @search:focus="$emit('search:focus')"
          >
            <template slot="no-options">
              <div class="smart-input__select-option">
                Ничего не найдено
              </div>
            </template>
            <template slot="option" slot-scope="option">
              <div class="smart-input__select-option">
                {{ option.label }}
              </div>
            </template>
          </v-select>
        </template>
        <template v-else>
          <v-select
            v-model="newValue"
            :disabled="disabled"
            :max-height="'200px'"
            :multiple="multiple"
            :options="optionsForSelect"
            class="smart-input__select"
          >
            <template slot="no-options">
              <div class="smart-input__select-option">
                Ничего не найдено
              </div>
            </template>
            <template slot="option" slot-scope="option">
              <div class="smart-input__select-option">
                {{ option.label }}
              </div>
            </template>
          </v-select>
        </template>
      </div>
      <div v-if="error" class="smart-input__error-message">
        {{ error }}
      </div>
    </div>
  </div>
</template>

<script>
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";

/**
 * Компонент выпадающего списка с возможностью поиска и установки нескольких значений.
 *
 * Обертка вокруг v-select. В общем случае компонент не универсальный и завязан на текущие кейсы в системе.
 * Необходимо проверять его работу в отдельных случаях. Возможно будет переписан на собственное решение.
 */
export default {
  components: {
    vSelect,
  },
  props: {
    /**
     * Значение, которое надо передавать через v-model.
     */
    value: {
      type: [String, Number, Array, Object],
      default: null,
    },
    /**
     * Заголовок поля.
     */
    caption: {
      type: String,
      default: "",
    },
    /**
     * Содержимое для placeholder в input-элемент.
     */
    placeholder: {
      type: String,
      default: "",
    },
    /**
     * Вариант размера элемента.
     *
     * @values s, m, l
     */
    size: {
      type: String,
      default: "m",
    },
    /**
     * Текст ошибки для поля.
     */
    error: {
      type: String,
      default: "",
    },
    /**
     * Флаг для размещения подписи и поля ввода на одной строке.
     */
    oneRow: {
      type: Boolean,
      default: false,
    },
    /**
     * Настройки для организации поиска значений на сервере.
     * Необходимо указать action в vuex, который вернет массив данных,
     * из которых можно будет идентифицировать запись по valueField
     * и отобразить его заголовок по labelField (строка либо массив строк, которые объединяются при выводе).
     * Опционально можно передать searchParams в виде объекта который распакуется в аргументы к action при его вызове,
     * и labelSeparator для указания символа, используемого в качестве разделителя между значениями labelField, если там массив.
     *
     * {
     *   action: "action/action",
     *   valueField: "id",
     *   labelField: "title",
     *   searchParams: {},
     *   labelSeparator: "|",
     * }
     *
     * Если поиск на сервере не нужен - можно ничего не передавать, значение по умолчанию null.
     * todo не описана сигнатура требуемого action
     */
    settingsRemoteSearch: {
      type: Object,
      default: null,
    },
    /**
     * Набор начальных опций для поиска в выпадающем списке.
     * Актуально когда уже есть имеющиеся значения в модели или когда поиск на сервере не нужен.
     *
     * Предполагаем что в случае без поиска на сервере - начальными опциями являются все допустимые опции для выпадающего списка.
     * Если поиск на сервере нужен и есть начальное значение модели, то в параметр можно передать массив из объектов,
     * для которого пройдет начальная обработка значений (которая происходит каждый раз при обработке значений пришедших с сервера).
     */
    initialOptions: {
      type: Array,
      default: () => ([]),
    },
    /**
     * Опция для установки нескольких значений.
     */
    multiple: {
      type: Boolean,
      default: false,
    },
    /**
     * Опция для отключения возможности ввода в поле.
     */
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      optionsForSelect: [],
      newValue: this.value,
    };
  },
  computed: {
    /**
     * Необходимость в поиске на сервере определяется по переданным опциям для выпадающего списка.
     *
     * @return {Boolean}
     */
    isNeededRemoteSearch() {
      return this.settingsRemoteSearch !== null;
    },
    /**
     * @return {String} Вернуть класс, применяемый для размера отступов select.
     */
    sizeClass() {
      const sizeClasses = {
        "l": "smart-input_large",
        "m": "smart-input_medium",
        "s": "smart-input_small",
      };
      return _.get(sizeClasses, this.size, "smart-input_medium");
    },
  },
  watch: {
    /**
     * Наблюдение за оригинальным значением для его синхронизации со внутренним значением компонента и отправкой события `@change`.
     * Работает только на обнуление значения, поскольку при работе с массивами значений, получаемых с удаленного поиска,
     * нужно много телодвижений делать чтоб восстановить их.
     */
    value() {
      if (this.value === null) {
        this.newValue = null;
        /**
         * Срабатывает при изменении значения со стороны родительского компонента.
         * @event change
         */
        this.$emit("change", null);
      }
    },
    /**
     * Наблюдение за новым значением выпадающего списка, и передача его id родительскому компоненту через `@input`.
     * Если по настройкам компонент может принимать множество значений - будет возвращаться массив из id.
     */
    newValue() {
      if (this.newValue === this.value) {
        return;
      }

      let returnedValue = null;
      if (this.multiple) {
        returnedValue = _.map(this.newValue, "id");
      } else {
        if (this.isNeededRemoteSearch) {
          returnedValue = this.newValue ? this.newValue.id : null;
        } else {
          returnedValue = this.newValue;
        }
      }
      /**
       * Срабатывает при вводе значения.
       * @event input
       */
      this.$emit("input", returnedValue);
    },
  },
  /**
   * В зависимости от переданных опций определяется функция отложенного поиска на сервере
   * и сразу вызывается поиск для отображения хоть каких-то начальных значений, если они не были переданы в компонент.
   *
   * Если уже были переданы начальные значения то их нужно преобразовать к правильному виду,
   * который пригоден и для работы в v-select, и для передачи конечного значения в родительский компонент,
   * с учетом опции multiple.
   */
  mounted() {
    let initialOptions = this.initialOptions;
    if (this.isNeededRemoteSearch) {
      this.debouncedRemoteSearch = _.debounce(this.remoteSearch, 350);
      // Приведение начального initialOptions к пригодному виду, если он предусматривает поиск на сервере.
      if (_.isEmpty(initialOptions)) {
        this.remoteSearch();
      } else {
        initialOptions = this.parserSelectOptions(initialOptions, this.settingsRemoteSearch.valueField, this.settingsRemoteSearch.labelField, this.settingsRemoteSearch.labelSeparator);
        if (this.multiple) {
          this.newValue = initialOptions;
        } else {
          this.newValue = initialOptions[0];
        }
      }
    }
    this.optionsForSelect = initialOptions;
  },
  methods: {
    /**
     * Преобразует список сырых данных, пришедших из сервера, предназначенных для поля select.
     * Вернет список с объектами со стандартизированными ключами для отображения видимых
     * и подстановки реальных значений.
     *
     * @param {Array} rawOptions
     * @param {String} fieldKey
     * @param {String} valueField
     * @param {String} labelSeparator
     * @return {Array}
     */
    parserSelectOptions(rawOptions, fieldKey, valueField, labelSeparator) {
      return rawOptions ? rawOptions.map((currentValue) => {
        if (Array.isArray(valueField)) {
          return {id: currentValue[fieldKey], label: _.join(_.values(_.pick(currentValue, valueField)), labelSeparator || ", ")};
        }
        return {id: currentValue[fieldKey], label: currentValue[valueField]};
      }) : [];
    },
    /**
     * Функция удаленного поиска. Нужна для передачи её в компонент v-select.
     * В соответствии с заданными настройками отправляет поисковый запрос на сервер и разбирает принятые результаты.
     *
     * @param {String} search
     * @param {Function} fnAfterLoad
     */
    remoteSearch(search = "", fnAfterLoad = null) {
      const searchParams = this.settingsRemoteSearch.searchParams || {};
      this.$store.dispatch(this.settingsRemoteSearch.action, {
        search,
        fields: [this.settingsRemoteSearch.valueField, ...(Array.isArray(this.settingsRemoteSearch.labelField) ? this.settingsRemoteSearch.labelField : [this.settingsRemoteSearch.labelField])],
        ...searchParams,
      })
        .then((rawOptions) => {
          this.$set(this, "optionsForSelect", this.parserSelectOptions(rawOptions, this.settingsRemoteSearch.valueField, this.settingsRemoteSearch.labelField, this.settingsRemoteSearch.labelSeparator));
          _.isFunction(fnAfterLoad) && fnAfterLoad();
        });
    },
  },
};
</script>

<style lang="scss">
@use "./scss/style.scss";
</style>
